// The auth-advisor session endpoints, in one place.
//
// Instance A owns the routes (backend/app/routes/authorizations.py and
// routes/auth_routes.py); the contract is docs/auth-advisor.md. Every path and
// every field name the advisor UI reads is named in this file and nowhere else,
// so reconciling with A's published JSON is an edit here rather than a hunt
// through the components. CSRF is attached by the wrapper index.js installs on
// window.fetch, so a plain fetch is correct.
//
// The older authorizations calls (list, alerts, payers, create/edit/retire) still
// live in their components; this module is the advisor sprint's surface only.

import {
  normalizeRun,
  normalizeRunbook,
  parseServerTime,
} from "../queue/api";

const API_URL = process.env.REACT_APP_API_URL || "";

// The vocabularies, kept in step with the migration's column comments. The server
// is authoritative and validates again; these only decide what the UI offers.
export const DISCIPLINES = ["SN", "PT", "OT", "ST", "MSW", "HHA", "other"];
export const LINE_UNITS = ["visits", "units"];
export const REQUEST_KINDS = ["renewal", "more_units", "initial"];
export const REQUEST_CHANNELS = ["portal", "edi_278", "email", "fax", "phone"];

export const KIND_LABELS = {
  renewal: "Renewal",
  more_units: "More units",
  initial: "Initial request",
};

export const ACTION_LABELS = {
  request_renewal: "Request renewal",
  request_more_units: "Request more units",
  request_initial: "Request initial auth",
};

// The advisor's action → the requested_kind a Mark requested writes by default.
export const ACTION_TO_KIND = {
  request_renewal: "renewal",
  request_more_units: "more_units",
  request_initial: "initial",
};

export const CHANNEL_LABELS = {
  portal: "Portal",
  edi_278: "Availity 278",
  email: "Email",
  fax: "Fax",
  phone: "Phone",
  none: "No auth needed",
};

export const TIMING_LABELS = {
  before_soc: "before SOC",
  at_soc: "at SOC",
  after_care: "after care ends",
  none: null,
};

// Never surface a server message we did not write. A returns {error: "..."} for
// refusals a biller can act on and a generic sentence for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  // A 200 is not a payload. A proxy's HTML page parses to null, and defaulting
  // that to [] would render "nothing to request" at exactly the moment we could
  // not check.
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function post(payload) {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const authBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/authorizations`;

// GET …/authorizations/advisor → {flags, waiting, schedule_available}, normalised.
// Refuses a body without both lists rather than rendering an all-clear.
export async function fetchAdvisor(practiceId) {
  const body = await request(
    `${authBase(practiceId)}/advisor`,
    {},
    "Could not check which authorizations need requesting.",
  );
  if (!Array.isArray(body.flags) || !Array.isArray(body.waiting)) {
    throw new Error(
      "The advisor response could not be read, so this list may be incomplete. Try again.",
    );
  }
  const flags = body.flags.map(normalizeFlag);
  // The server sorts by score; sorting again is cheap and keeps the order right
  // if it ever does not. Array.prototype.sort is stable, so equal scores keep the
  // server's order.
  flags.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  return {
    flags,
    waiting: body.waiting.map(normalizeWaiting),
    // Decisions a route's conditions turned off. Optional: absent reads as none.
    suppressed: Array.isArray(body.suppressed)
      ? body.suppressed.map(normalizeSuppressed)
      : [],
    // The missing-auth scan stopped at its row limit: the list is a floor.
    truncated: body.truncated === true,
    // null = the server did not say, which is not the same as "no schedule".
    scheduleAvailable:
      typeof body.schedule_available === "boolean"
        ? body.schedule_available
        : null,
  };
}

// POST …/authorizations/<id>/requested {kind, channel, ref?} → {authorization}.
// Takes the auth off the action list and into "Waiting on payer". The server
// refuses (409) an auth that is already pending.
export function markRequested(practiceId, authId, { kind, channel, ref }) {
  const payload = { kind, channel };
  const trimmed = (ref || "").trim();
  if (trimmed) payload.ref = trimmed;
  return request(
    `${authBase(practiceId)}/${authId}/requested`,
    post(payload),
    "Could not mark this authorization as requested.",
  );
}

export const OUTCOMES = ["approved", "denied", "more_info", "withdrawn"];

export const OUTCOME_LABELS = {
  pending: "Waiting on payer",
  approved: "Approved",
  denied: "Denied",
  more_info: "Payer asked for more info",
  withdrawn: "Withdrawn",
};

export const TIMINGS = ["before_soc", "at_soc", "after_care", "none"];
export const ROUTE_CHANNELS = [...REQUEST_CHANNELS, "none"];

// POST …/authorizations/<id>/packet → {packet, text}. The packet is built for a
// person to copy into a portal, an email or a fax cover; Atlas never sends it.
export async function fetchPacket(practiceId, authId) {
  const body = await request(
    `${authBase(practiceId)}/${authId}/packet`,
    post({}),
    "Could not build the request packet.",
  );
  if (typeof body.text !== "string" || !body.text.trim()) {
    throw new Error("The packet came back empty, so there is nothing to copy.");
  }
  return { text: body.text, packet: body.packet || null };
}

// POST …/authorizations/<id>/outcome {outcome, ref?, note?, valid_from?, valid_to?,
// approved_units?} → {authorization}. Written once: 409 when nothing is pending or
// an outcome is already recorded. A window or units may only accompany an approval;
// `approved_units` [{discipline, authorized}] merges into the lines — the
// disciplines named are set, the rest kept.
export function recordOutcome(
  practiceId,
  authId,
  { outcome, ref, note, validFrom, validTo, approvedUnits },
) {
  const payload = { outcome };
  const r = (ref || "").trim();
  const n = (note || "").trim();
  if (r) payload.ref = r;
  if (n) payload.note = n;
  if (outcome === "approved") {
    if (validFrom) payload.valid_from = validFrom;
    if (validTo) payload.valid_to = validTo;
    if (approvedUnits && approvedUnits.length) {
      payload.approved_units = approvedUnits.map((l) => ({
        discipline: l.discipline,
        authorized: l.authorized,
      }));
    }
  }
  return request(
    `${authBase(practiceId)}/${authId}/outcome`,
    post(payload),
    "Could not record the payer's answer.",
  );
}

const routesBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/auth-routes`;

// GET …/auth-routes → {routes, condition_facts}; the routes, normalised.
export async function fetchAuthRoutes(practiceId) {
  const body = await request(
    routesBase(practiceId),
    {},
    "Could not load the authorization routes.",
  );
  if (!Array.isArray(body.routes)) {
    throw new Error(
      "The routes response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.routes.map(normalizeRouteRow);
}

// GET …/auth-routes/unmatched → {payers: [{payer_name, claims, authorizations,
// patients}]}, most frequent first: payer strings on this practice's claims and
// authorizations that no route answers to. Strings and counts only.
export async function fetchUnmatchedPayers(practiceId) {
  const body = await request(
    `${routesBase(practiceId)}/unmatched`,
    {},
    "Could not list the payer names that match no route.",
  );
  if (!Array.isArray(body.payers)) {
    throw new Error("The unmatched payer list could not be read.");
  }
  return body.payers.map(normalizeUnmatched).filter((p) => p.payerName);
}

// ---- Auth-status runbooks and runs, per route (A phase 2, session) ----
//
// An auth-status runbook is a `portal_runbooks` row with portal_key
// `auth:<authority slug>`; a run is a `skill_runs` row with skill `auth-status`.
// The RUNBOOK and RUN shapes are the submit queue's, so they are normalised by the
// queue's helpers; a run adds `dry_run`.

// GET …/auth-routes/<route_id>/runbook → {authority, portal_key, runbook, latest, history}.
export async function fetchRouteRunbook(practiceId, routeId) {
  const body = await request(
    `${routesBase(practiceId)}/${routeId}/runbook`,
    {},
    "Could not load this route's runbook.",
  );
  if (!Array.isArray(body.history)) {
    throw new Error(
      "The runbook response could not be read, so it may be incomplete. Try again.",
    );
  }
  return {
    portalKey: body.portal_key || null,
    // v0.2 sends both `latest` and `runbook` (the same object).
    latest:
      body.latest || body.runbook
        ? normalizeRunbook(body.latest || body.runbook)
        : null,
    history: body.history.map(normalizeRunbook),
  };
}

// POST …/auth-routes/<route_id>/runbook/<runbook_id>/approve → {runbook}. Admin.
// 409 when already approved or when a newer version exists; 404 when the runbook
// is another route's.
// Approval is a person saying they read this version; the server never takes it
// from a skill.
export async function approveRouteRunbook(practiceId, routeId, runbookId) {
  const body = await request(
    `${routesBase(practiceId)}/${routeId}/runbook/${runbookId}/approve`,
    post({}),
    "Could not approve the runbook.",
  );
  return body.runbook ? normalizeRunbook(body.runbook) : null;
}

// GET …/auth-routes/<route_id>/runs → {runs: [RUN]}, the last 20, newest first. A
// run counts toward a route only when it was opened with a runbook from that
// route's lineage.
export async function fetchRouteRuns(practiceId, routeId) {
  const body = await request(
    `${routesBase(practiceId)}/${routeId}/runs`,
    {},
    "Could not load this route's auth-status runs.",
  );
  if (!Array.isArray(body.runs)) {
    throw new Error(
      "The runs response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.runs.map((r) => ({
    ...normalizeRun(r),
    dryRun: Boolean(r && r.dry_run === true),
  }));
}

// GET …/auth-routes/runs → {runs: [RUN]}, the practice's last 20 auth-status runs,
// newest first — including runs opened without a runbook, which no route's own
// list shows.
export async function fetchPracticeAuthRuns(practiceId) {
  const body = await request(
    `${routesBase(practiceId)}/runs`,
    {},
    "Could not load the auth-status runs.",
  );
  if (!Array.isArray(body.runs)) {
    throw new Error(
      "The runs response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.runs.map((r) => ({
    ...normalizeRun(r),
    dryRun: Boolean(r && r.dry_run === true),
  }));
}

// POST …/auth-routes/seed {dry_run} → {dry_run, created, existing, unmatched}.
// Copies David's default routes the practice does not already have; never
// overwrites a route or alias it has. A dry run writes nothing.
export async function seedAuthRoutes(practiceId, { dryRun }) {
  const body = await request(
    `${routesBase(practiceId)}/seed`,
    post({ dry_run: dryRun }),
    "Could not copy the default routes.",
  );
  const names = (v) =>
    Array.isArray(v) ? v.filter((n) => typeof n === "string" && n.trim()) : [];
  return {
    dryRun: body.dry_run !== false,
    created: names(body.created),
    existing: names(body.existing),
  };
}

function routeBody(route) {
  return {
    authority_name: route.authorityName.trim(),
    aliases: route.aliases,
    channel: route.channel,
    target: (route.target || "").trim() || null,
    timing: route.timing || null,
    lead_days: route.leadDays,
    notes: (route.notes || "").trim() || null,
  };
}

// POST …/auth-routes → {route}; PUT …/auth-routes/<id> → {route}. `conditions`
// is never sent: it is rules-engine JSON the seed writes, not a form field.
export async function saveAuthRoute(practiceId, route) {
  const editing = route.id != null;
  const body = await request(
    editing ? `${routesBase(practiceId)}/${route.id}` : routesBase(practiceId),
    {
      method: editing ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(routeBody(route)),
    },
    "Could not save the route.",
  );
  return body.route ? normalizeRouteRow(body.route) : null;
}

// A link Atlas will render for a route target, or null. Only http(s): a target
// is typed by an admin or written by a seed, and `javascript:` in an href runs
// in the practice's session. "www.availity.com" (how David's seed writes it) is
// read as https.
export function safeHref(target) {
  if (typeof target !== "string") return null;
  const t = target.trim();
  if (!t || /\s/.test(t)) return null;
  const withScheme = /^www\./i.test(t) ? `https://${t}` : t;
  let url;
  try {
    url = new URL(withScheme);
  } catch (err) {
    return null;
  }
  return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
}

// ---- Shape helpers. The only places a response field name is read. ----

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};

// A reason or caveat is a sentence. Accept {text}/{reason} objects too, and drop
// anything that is not readable rather than rendering "[object Object]".
function sentences(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) =>
      typeof r === "string" ? r : r && (r.text || r.reason || r.message),
    )
    .filter((s) => typeof s === "string" && s.trim())
    .map((s) => s.trim());
}

export function normalizeRoute(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (!raw.channel) return null;
  return {
    channel: raw.channel,
    target: raw.target || null,
    timing: raw.timing || null,
  };
}

// `missing:<past|upcoming>:<patient_id>:<practice_payer_id>` — A's documented key
// for a missing flag. 0.1 also puts both ids on the flag; the key is read only as
// a fallback when they are absent.
function missingKeyParts(key) {
  const parts = typeof key === "string" ? key.split(":") : [];
  if (parts[0] !== "missing" || parts.length !== 4) return {};
  const id = (v) => (/^\d+$/.test(v) ? Number(v) : null);
  return { patientId: id(parts[2]), practicePayerId: id(parts[3]) };
}

export function normalizeFlag(raw) {
  const f = raw || {};
  const subject = f.subject && typeof f.subject === "object" ? f.subject : {};
  const fromKey = missingKeyParts(f.key);
  return {
    // A's stable key ("auth:12", or one per patient and payer for a missing auth).
    key: f.key || null,
    authId: f.auth_id ?? null,
    authNumber: f.auth_number || null,
    patientToken: f.patient_token || null,
    patientName: f.patient_name || subject.patient_name || null,
    authority: f.authority || null,
    payerName: f.payer_name || subject.payer_name || null,
    // Unknown, not a guess: an action we cannot read is not offered a button.
    action: ACTION_LABELS[f.action] ? f.action : "unknown",
    kinds: Array.isArray(f.kinds)
      ? f.kinds.filter((k) => typeof k === "string")
      : [],
    reasons: sentences(f.reasons),
    caveats: sentences(f.caveats),
    score: num(f.score),
    // null = not counted (an uncapped auth), never 0.
    used: num(f.used),
    authorized: num(f.authorized),
    route: normalizeRoute(f.route),
    packetAvailable: f.packet_available === true,
    // Set when a denied / more_info outcome brought this auth back to the top,
    // with what the payer said.
    outcome: f.outcome || null,
    outcomeNote: f.outcome_note || null,
    // What a missing flag knows about the visit that needs covering, in the shape
    // the page's "Add authorization" prefill takes. A puts the patient on the
    // flag and the visit in `subject` ({claim_id, appointment_id,
    // date_of_service, visits}).
    subject: {
      patient_id: f.patient_id ?? subject.patient_id ?? fromKey.patientId ?? null,
      patient_name: f.patient_name || subject.patient_name || null,
      practice_payer_id:
        f.practice_payer_id ??
        subject.practice_payer_id ??
        fromKey.practicePayerId ??
        null,
      payer_name: f.payer_name || subject.payer_name || null,
      date_of_service: subject.date_of_service || null,
    },
  };
}

export function normalizeSuppressed(raw) {
  const x = raw || {};
  return {
    key: x.key || null,
    authId: x.auth_id ?? null,
    authority: x.authority || null,
    wouldHave: sentences(x.would_have),
    reasons: sentences(x.reasons),
  };
}

// Whole days between a server timestamp and now, or null.
export function daysSince(value, now = new Date()) {
  const when = parseServerTime(value);
  if (!when) return null;
  return Math.max(0, Math.floor((now.getTime() - when.getTime()) / 86400000));
}

export function normalizeWaiting(raw, now = new Date()) {
  const w = raw || {};
  const days = num(w.days_pending);
  return {
    authId: w.auth_id ?? null,
    authNumber: w.auth_number || null,
    patientName: w.patient_name || null,
    authority: w.authority || null,
    payerName: w.payer_name || null,
    requestedAt: w.requested_at || null,
    requestedBy: w.requested_by || null,
    kind: w.requested_kind || null,
    channel: w.requested_channel || null,
    ref: w.requested_ref || null,
    daysPending: days ?? daysSince(w.requested_at, now),
  };
}

// A discipline line on a serialized authorization. `used`, `scheduled` and
// `remaining` are read-side only; null means not counted (scheduled needs the
// schedule capability), never zero.
export function normalizeLine(raw) {
  const l = raw || {};
  return {
    id: l.id ?? null,
    discipline: l.discipline || "other",
    authorized: num(l.authorized),
    unit: LINE_UNITS.includes(l.unit) ? l.unit : "visits",
    used: num(l.used),
    scheduled: num(l.scheduled),
    remaining: num(l.remaining),
  };
}

// Distinct visit dates no claim code could assign to a discipline (0.3); null
// when the authorization has no lines or the server did not say.
export function unattributedFrom(row) {
  const n = num(row && row.unattributed_visits);
  return n == null ? null : n;
}

export function linesFrom(row) {
  const raw = row && row.lines;
  return Array.isArray(raw) ? raw.map(normalizeLine) : [];
}

// The lines as the create/edit body sends them. `authorized` null = no cap.
export function linesBody(lines) {
  return lines.map((l) => ({
    discipline: l.discipline,
    authorized: l.authorized,
    unit: l.unit,
  }));
}

function aliasList(raw) {
  // A's column is a JSON array in a Text column; accept the parsed array or the
  // raw string, and nothing else.
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch (err) {
      list = [];
    }
  }
  return Array.isArray(list)
    ? list.filter((a) => typeof a === "string" && a.trim()).map((a) => a.trim())
    : [];
}

export function normalizeRouteRow(raw) {
  const r = raw || {};
  return {
    id: r.id ?? null,
    authorityName: r.authority_name || "",
    aliases: aliasList(r.aliases),
    channel: ROUTE_CHANNELS.includes(r.channel) ? r.channel : "none",
    target: r.target || "",
    timing: TIMINGS.includes(r.timing) ? r.timing : "",
    leadDays: num(r.lead_days),
    notes: r.notes || "",
    // Shown, never edited here. An unreadable blob still counts as present.
    hasConditions:
      r.conditions_invalid === true ||
      (r.conditions != null && r.conditions !== ""),
    conditionsInvalid: r.conditions_invalid === true,
    lastCheckedAt: r.last_checked_at || null,
  };
}

// The requested / outcome state on a serialized authorization row.
export function requestStateFrom(row) {
  const r = row || {};
  return {
    requestedAt: r.requested_at || null,
    kind: r.requested_kind || null,
    channel: r.requested_channel || null,
    ref: r.requested_ref || null,
    outcome: r.outcome || null,
    outcomeAt: r.outcome_at || null,
    outcomeNote: r.outcome_note || null,
  };
}

export function normalizeUnmatched(raw) {
  const u = raw || {};
  return {
    payerName: typeof u.payer_name === "string" ? u.payer_name.trim() : "",
    claims: num(u.claims) ?? 0,
    authorizations: num(u.authorizations) ?? 0,
    patients: num(u.patients) ?? 0,
  };
}
